import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate,
} from 'react-router-dom'
import {
  ApiError, api, clearPendingInvite, clearSession, loadPendingInvite, loadSession,
  setPendingInvite, setSession, whenSignedOut, type Me, type Organization,
} from './api'
import { vocabulary } from './vocabulary'
import SignIn from './screens/SignIn'
import CreateOrganization from './screens/CreateOrganization'
import Home from './screens/Home'
import Devices from './screens/Devices'
import Servers from './screens/Servers'
import Team from './screens/Team'
import Labels from './screens/Labels'

/**
 * The session arrives in the URL fragment after Google sends the browser back — a fragment never
 * reaches a server, and it is stripped from history the moment it is read, so the token does not
 * sit in the address bar or the back stack.
 */
function claimSessionFromUrl(): boolean {
  const hash = window.location.hash
  if (!hash.startsWith('#session=')) return false
  const params = new URLSearchParams(hash.slice(1))
  const token = params.get('session')
  if (!token) return false
  setSession(token)
  window.history.replaceState(null, '', window.location.pathname + window.location.search)
  return true
}

/**
 * An invite link carries its token the same way (`/join#invite=…`), and the token must survive
 * the Google round-trip — sign-in navigates away and returns with the fragment replaced by the
 * session. So the invite is claimed into sessionStorage the moment the app boots, on ANY path,
 * and redeemed once a session exists.
 */
function claimInviteFromUrl(): void {
  const hash = window.location.hash
  if (!hash.includes('invite=')) return
  const params = new URLSearchParams(hash.slice(1))
  const token = params.get('invite')
  if (!token) return
  setPendingInvite(token)
  window.history.replaceState(null, '', window.location.pathname + window.location.search)
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  )
}

function Shell() {
  const [me, setMe] = useState<Me | null>(null)
  const [ready, setReady] = useState(false)
  const [signedOutNote, setSignedOutNote] = useState<string | undefined>()
  const [invite, setInvite] = useState<string | null>(null)
  const navigate = useNavigate()

  const refresh = useCallback(async () => {
    if (!loadSession()) {
      setMe(null)
      setReady(true)
      return
    }
    try {
      setMe(await api.me())
    } catch (failure) {
      setMe(null)
      if (failure instanceof ApiError && !failure.signedOut) {
        setSignedOutNote(failure.message)
      }
    } finally {
      setReady(true)
    }
  }, [])

  useEffect(() => {
    claimSessionFromUrl()
    claimInviteFromUrl()
    setInvite(loadPendingInvite())
    whenSignedOut(() => {
      setMe(null)
      setSignedOutNote('Your session ended. Sign in to continue.')
    })
    void refresh()
  }, [refresh])

  async function signOut() {
    try {
      await api.signOut()
    } catch {
      /* the session is going away locally regardless */
    }
    clearSession()
    setMe(null)
    setSignedOutNote(undefined)
    navigate('/')
  }

  if (!ready) {
    return <div className="center" style={{ minHeight: '100%' }} aria-busy="true" />
  }

  if (!me) return <SignIn error={signedOutNote} invited={invite !== null} />

  // Precedence is the domain rule: an invited person JOINS; only an uninvited
  // person founds an organization. The pending invite is checked before the
  // no-organization branch, so an invitee is never asked to name an org.
  if (invite) {
    return (
      <div className="shell">
        <TopBar email={me.email} picture={me.picture_url} onSignOut={signOut} />
        <JoinOrganization
          token={invite}
          onDone={() => {
            setInvite(null)
            void refresh()
          }}
        />
      </div>
    )
  }

  if (me.organizations.length === 0) {
    return (
      <div className="shell">
        <TopBar email={me.email} picture={me.picture_url} onSignOut={signOut} />
        <CreateOrganization onCreated={() => void refresh()} />
      </div>
    )
  }

  const organization = me.organizations[0]!

  return (
    <div className="shell">
      <TopBar email={me.email} picture={me.picture_url} onSignOut={signOut} organization={organization} />
      <div className="body">
        <Nav />
        <main className="main">
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <Home
                  accountId={organization.account_id}
                  organizationName={organization.display_name ?? 'Your organization'}
                />
              }
            />
            <Route
              path="/devices"
              element={
                <Devices
                  accountId={organization.account_id}
                  canSetUp={organization.permissions.includes('delegator-admin')}
                />
              }
            />
            <Route
              path="/servers"
              element={
                <Servers
                  accountId={organization.account_id}
                  canConnect={organization.permissions.includes('developer')}
                />
              }
            />
            <Route
              path="/team"
              element={
                <Team
                  accountId={organization.account_id}
                  canManage={organization.permissions.includes('owner-admin')}
                />
              }
            />
            <Route
              path="/labels"
              element={<Labels accountId={organization.account_id} />}
            />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </main>
      </div>
      <TabBar />
    </div>
  )
}

const TABS = vocabulary.nav

function Nav() {
  const { pathname } = useLocation()
  return (
    <nav className="side" aria-label={vocabulary.shell.navAria}>
      {TABS.map((tab) => (
        <Link key={tab.to} to={tab.to} className={pathname === tab.to ? 'on' : undefined}>
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

function TabBar() {
  const { pathname } = useLocation()
  return (
    <nav className="tabbar" aria-label={vocabulary.shell.navAria}>
      {TABS.map((tab) => (
        <Link key={tab.to} to={tab.to} className={pathname === tab.to ? 'on' : undefined}>
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

function TopBar({ email, picture, organization, onSignOut }: {
  email: string
  picture?: string
  organization?: Organization
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  return (
    <header className="topbar">
      <picture>
        <source srcSet="/wordmark-white.png" media="(prefers-color-scheme: dark)" />
        <img className="wordmark-img" src="/wordmark-navy.png" alt={vocabulary.shell.brandAlt} />
      </picture>
      <div ref={box} style={{ position: 'relative' }}>
        <button
          type="button"
          className="icon-btn"
          style={{ padding: 0 }}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={vocabulary.shell.accountAria}
        >
          {picture
            ? <img className="avatar" src={picture} alt="" referrerPolicy="no-referrer" />
            : <span className="avatar">{email.charAt(0).toUpperCase()}</span>}
        </button>
        {open && (
          <div className="menu" role="menu">
            <div style={{ padding: '8px 10px' }}>
              <div className="small">{email}</div>
              {organization && (
                <div className="tiny faint">{organization.display_name ?? organization.account_id}</div>
              )}
            </div>
            <div className="sep" />
            <a href="https://oslp.in" target="_blank" rel="noreferrer" role="menuitem">
              {vocabulary.shell.aboutOslp}
            </a>
            <div className="sep" />
            <button type="button" onClick={onSignOut} role="menuitem">{vocabulary.shell.signOut}</button>
          </div>
        )}
      </div>
    </header>
  )
}

/**
 * A teammate arriving on an invite link: the token was claimed at boot and sign-in already
 * happened, so this only redeems. The stash is cleared the moment the attempt settles — success
 * or failure — so a reload never replays an expired invite; failure falls through to wherever
 * the person would otherwise be (the naming screen for a first-timer, home for a member).
 */
function JoinOrganization({ token, onDone }: { token: string; onDone: () => void }) {
  const [state, setState] = useState<'working' | 'joined' | 'failed'>('working')
  const [message, setMessage] = useState('')

  useEffect(() => {
    api.redeemTeamInvite(token)
      .then((joined) => {
        setMessage(`${vocabulary.shell.joinedPrefix}${joined.account_name ?? vocabulary.shell.theOrganization}${vocabulary.shell.joinedSuffix}`)
        setState('joined')
      })
      .catch((failure) => {
        setState('failed')
        setMessage(failure instanceof ApiError ? failure.message : vocabulary.shell.joinFailed)
      })
      .finally(() => clearPendingInvite())
  }, [token])

  if (state === 'working') return <p className="small muted">{vocabulary.shell.joining}</p>
  return (
    <div className="center" style={{ minHeight: '60%' }}>
      <div className="stack" style={{ gap: 12, maxWidth: 420 }}>
        <p className={state === 'failed' ? 'banner stop' : 'banner'}>{message}</p>
        <button type="button" className="btn" onClick={onDone}>
          {state === 'joined' ? vocabulary.shell.goToOrg : vocabulary.shell.continueWord}
        </button>
      </div>
    </div>
  )
}
